import React from "react";

// 6-pointed Chicago flag star — standalone brand asset
export const ChicagoStar = ({ size = 14, className = "", color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color || "currentColor"} className={className} aria-hidden>
    <path d="M12 1.5l2.4 6.3 6.6.4-5.1 4.3 1.7 6.5L12 15.7 6.4 19l1.7-6.5L3 8.2l6.6-.4L12 1.5z"/>
  </svg>
);
export const ChicagoStars = ({ count = 4, size = 14, className = "", color }) => (
  <span className={`cod-stars ${className}`} aria-hidden>
    {Array.from({ length: count }).map((_, i) => <ChicagoStar key={`cstar-${i}`} size={size} color={color}/>)}
  </span>
);

// COD letter monogram — used as standalone brand mark
export const CODMonogram = ({ size = 36, className = "", color }) => (
  <span className={`font-serif font-bold tracking-tight ${className}`}
    style={{ fontSize: size, color: color || "var(--cod-navy)", lineHeight: 1 }} aria-hidden>COD</span>
);

// Chicago skyline silhouette — section accent (decorative SVG inspired by logo)
export const SkylineDivider = ({ color = "var(--cod-navy)", className = "" }) => (
  <svg viewBox="0 0 1200 80" className={className} preserveAspectRatio="none" aria-hidden style={{ width: "100%", height: 56 }}>
    <path fill={color} d="M0 80 L0 50 L40 50 L40 35 L70 35 L70 25 L90 25 L90 12 L100 6 L110 12 L110 25 L135 25 L135 40 L165 40 L165 22 L185 22 L185 15 L200 8 L215 15 L215 22 L235 22 L235 38 L260 38 L260 28 L290 28 L290 18 L310 18 L310 8 L325 0 L340 8 L340 18 L365 18 L365 30 L395 30 L395 42 L425 42 L425 28 L455 28 L455 12 L475 12 L475 4 L490 0 L505 4 L505 12 L525 12 L525 22 L560 22 L560 38 L595 38 L595 28 L625 28 L625 14 L650 14 L650 6 L665 0 L680 6 L680 14 L705 14 L705 28 L740 28 L740 42 L770 42 L770 24 L800 24 L800 14 L820 14 L820 8 L835 0 L850 8 L850 14 L870 14 L870 30 L900 30 L900 22 L935 22 L935 36 L965 36 L965 22 L995 22 L995 14 L1015 14 L1015 6 L1030 0 L1045 6 L1045 14 L1065 14 L1065 28 L1100 28 L1100 40 L1135 40 L1135 28 L1165 28 L1165 42 L1200 42 L1200 80 Z"/>
  </svg>
);

// SVG recreation of the COD mark — native transparency, scales to any size, prints cleanly
export const CODLogoSVG = ({ size = 56, className = "", navy = "#14366B", red = "#C8102E" }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" className={className} aria-hidden role="img">
    {/* Stylized Chicago skyline */}
    <g fill={navy}>
      <path d="M30 90 L30 70 L42 70 L42 55 L52 55 L52 40 L58 30 L64 40 L64 55 L78 55 L78 65 L92 65 L92 45 L100 30 L108 45 L108 65 L122 65 L122 50 L136 50 L136 60 L148 60 L148 72 L170 72 L170 90 Z"/>
    </g>
    {/* COD lettering */}
    <text x="100" y="148" textAnchor="middle" fontFamily="Fraunces, Georgia, serif"
      fontWeight="800" fontSize="56" fill={navy} letterSpacing="-2">COD</text>
    {/* Four Chicago stars */}
    <g fill={red}>
      {[58, 84, 110, 136].map((x, i) => (
        <path key={`flag-star-${x}`} d={`M${x} 175 l3.2 8.4 8.8.5 -6.8 5.8 2.3 8.7 -7.5 -4.4 -7.5 4.4 2.3 -8.7 -6.8 -5.8 8.8 -.5z`}/>
      ))}
    </g>
  </svg>
);

export const CODLogoMark = ({ size = 44, onLight = true }) => (
  <img
    src="/cod-logo.png"
    alt="Chicago Opportunity Desk"
    style={{ height: size, width: size, objectFit: "contain",
             mixBlendMode: onLight ? "multiply" : "normal" }}
    data-testid="cod-logo-mark"
  />
);

export const CODLogo = ({ size = 44, showText = true, variant = "dark" }) => (
  <div className="flex items-center gap-3" data-testid="cod-logo">
    <CODLogoMark size={size} onLight={variant !== "light"}/>
    {showText && (
      <div className="flex flex-col items-start" style={{ lineHeight: 1.05 }}>
        <div className="font-serif font-semibold tracking-tight whitespace-nowrap"
          style={{ color: variant === "light" ? "white" : "var(--cod-navy)", fontSize: "1.05rem" }}>
          <span className="block" style={{ fontSize: "0.78em", letterSpacing: "0.02em" }}>Chicago</span>
          <span className="block">Opportunity Desk</span>
        </div>
        <div className={`text-[9px] uppercase tracking-[0.14em] mt-1 whitespace-nowrap ${variant==="light"?"text-white/80":"text-slate-500"}`}>
          Pathways · Plans · Progress
        </div>
      </div>
    )}
  </div>
);
